/**
 * JST（Asia/Tokyo）を基準にした時刻ユーティリティ（要件定義 8 章）。
 *
 * 日本標準時は年間を通じて UTC+9 で固定なので、
 * タイムゾーンライブラリを入れず固定オフセットで計算する。
 */

import type { Weekday } from './types.ts'
import { WEEKDAYS } from './types.ts'

export const JST_OFFSET_MINUTES = 9 * 60
const JST_OFFSET_MS = JST_OFFSET_MINUTES * 60 * 1000

const HOUR_MS = 60 * 60 * 1000
export const DAY_MS = 24 * HOUR_MS

export type JstParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  weekday: Weekday
}

const pad = (value: number, length = 2): string => String(value).padStart(length, '0')

/** epoch ミリ秒を JST の各要素に分解する */
export const jstParts = (epochMs: number): JstParts => {
  const shifted = new Date(epochMs + JST_OFFSET_MS)
  // 月曜はじまりに直す（getUTCDay は日曜が 0）
  const weekday = WEEKDAYS[(shifted.getUTCDay() + 6) % 7] as Weekday
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
    weekday,
  }
}

/** epoch ミリ秒を JST の ISO8601 文字列にする（例: 2026-08-24T10:00:00+09:00） */
export const formatJst = (epochMs: number): string => {
  const p = jstParts(epochMs)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}+09:00`
}

/** JST の日付（YYYY-MM-DD）と時刻から epoch ミリ秒を得る */
export const jstDateTime = (dateOnly: string, hour = 0, minute = 0, second = 0): number =>
  Date.parse(`${dateOnly}T${pad(hour)}:${pad(minute)}:${pad(second)}+09:00`)

/** JST の日付（YYYY-MM-DD）に変換する */
export const jstDateOnly = (epochMs: number): string => {
  const p = jstParts(epochMs)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** その時刻が属する週（JST の月曜日）の日付を返す */
export const jstWeekStart = (epochMs: number): string => {
  const p = jstParts(epochMs)
  const index = WEEKDAYS.indexOf(p.weekday)
  const midnight = jstDateTime(jstDateOnly(epochMs))
  return jstDateOnly(midnight - index * DAY_MS)
}

/** 週の開始（月曜 00:00 JST）の epoch ミリ秒 */
export const weekStartMs = (week: string): number => jstDateTime(week)

/** 週の終わり（次の月曜 00:00 JST）の epoch ミリ秒 */
export const weekEndMs = (week: string): number => weekStartMs(week) + 7 * DAY_MS

/** 秒を mm:ss / h:mm:ss 形式にする（番組表の表示用） */
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** JST の時刻表示（例: 10:32） */
export const formatJstClock = (epochMs: number): string => {
  const p = jstParts(epochMs)
  return `${pad(p.hour)}:${pad(p.minute)}`
}
